function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Autocomplete, Typography, TextButton, getOptionLabel, getOptionSelected, useOverlayContext } from 'seagull';
import { Plus } from 'seagull/icons';
import ServiceBuilder from "../../ServiceCreation/ServiceBuilder/ServiceBuilder";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { makeFailure, makeSuccess, sortAlphabetically } from "../../../infra/utils";
import { serviceTemplateModel } from "../../../models";
import { AgreementDataModel } from "../../../models/AgreementDataModel";
import { genericConnector } from "../../../utils/react-hook-form.utils";
import { useServiceTemplatesForBilling } from "./hooks/useServiceTemplatesForBilling";
import { jsx as _jsx } from "react/jsx-runtime";
export let ServiceTemplateOptionType = /*#__PURE__*/function (ServiceTemplateOptionType) {
  ServiceTemplateOptionType["Service"] = "service";
  ServiceTemplateOptionType["NewService"] = "new-service";
  ServiceTemplateOptionType["EmptySearch"] = "empty-search";
  ServiceTemplateOptionType["IncompleteSearch"] = "incomplete-search";
  return ServiceTemplateOptionType;
}({});
export function getServiceTemplateOptions(serviceTemplates) {
  let excludeBundles = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  const options = (excludeBundles ? serviceTemplates.filter(st => !(st !== null && st !== void 0 && st.bundle)) : serviceTemplates).map(st => ({
    value: st.id,
    label: st.name,
    data: st,
    type: ServiceTemplateOptionType.Service
  }));
  return sortAlphabetically(options, 'label', {
    ascending: true,
    ignoreCase: true
  });
}
export const ServiceTemplateInputTestIds = pathgen('ServiceTemplateInputTestIds');
export const ServiceTemplateInput = props => {
  var _value$label;
  const {
    testId,
    applyServiceChange,
    renderProps,
    relationship,
    menuWidth,
    excludeBundles = true
  } = props;
  const [currentServiceTemplateOption, setCurrentServiceTemplateOption] = useState(null);
  const serviceTemplateApi = useServiceTemplateApi();
  const overlayContext = useOverlayContext();
  const {
    field
  } = renderProps;
  const {
    serviceTemplates,
    isLoading,
    error: serviceTemplatesError,
    refresh: refreshServiceTemplates
  } = useServiceTemplatesForBilling({
    refreshOnMount: true
  });
  const serviceTemplateOptions = useMemo(() => {
    return getServiceTemplateOptions(serviceTemplates, excludeBundles);
  }, [serviceTemplates, excludeBundles]);
  const value = currentServiceTemplateOption;
  const valueLabel = (_value$label = value === null || value === void 0 ? void 0 : value.label) !== null && _value$label !== void 0 ? _value$label : '';
  const applyServiceOptionChange = newServiceOption => {
    setCurrentServiceTemplateOption(newServiceOption);
    const service = newServiceOption === null || newServiceOption === void 0 ? void 0 : newServiceOption.data;
    field.onChange(service === null || service === void 0 ? void 0 : service.id);
    applyServiceChange === null || applyServiceChange === void 0 || applyServiceChange(service);
  };
  const openCreateServiceTemplateModal = async () => {
    let newService;
    const _onSave = async service => {
      try {
        const {
          id
        } = await serviceTemplateApi.create(serviceTemplateModel.fromService(service));
        const {
          data: newServiceTemplates
        } = await refreshServiceTemplates();
        newService = newServiceTemplates === null || newServiceTemplates === void 0 ? void 0 : newServiceTemplates.find(s => s.id === id);
        overlayContext.showSuccessToast('Service created successfully', 'service-created-success');
        return makeSuccess({
          id
        });
      } catch (error) {
        overlayContext.showErrorToast('Failed to create service', 'service-creation-failed');
        return makeFailure(error);
      }
    };
    await overlayContext.showModal({
      transparentBackdrop: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread(_objectSpread({}, p), {}, {
        serviceNameBlacklist: serviceTemplates.map(s => s.name),
        agreementData: AgreementDataModel.fromRelationship(relationship),
        serviceOrigin: 'template',
        onSave: s => _onSave(s)
      }))
    });
    return {
      newService
    };
  };
  if (serviceTemplatesError) throw serviceTemplatesError;
  return /*#__PURE__*/_jsx(Autocomplete, {
    selectOnFocus: true,
    size: "small",
    menuWidth: menuWidth,
    forcePopupIcon: true,
    "data-testid": testId !== null && testId !== void 0 ? testId : ServiceTemplateInputTestIds.ServiceTemplateInput(),
    textFieldProps: {
      inputRef: field.ref,
      error: genericConnector(renderProps).showErrors
    },
    loading: isLoading,
    options: serviceTemplateOptions,
    renderOption: option => {
      if ((option === null || option === void 0 ? void 0 : option.type) === ServiceTemplateOptionType.NewService) {
        return /*#__PURE__*/_jsx(TextButton, {
          "data-testid": ServiceTemplateInputTestIds.NewServiceOption(),
          underline: "none",
          prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
          children: "New service"
        });
      }
      return /*#__PURE__*/_jsx(Typography, {
        as: "p",
        ellipsis: true,
        children: option.label
      });
    },
    filterOptions: options => {
      const NO_MATCHES_OPTION = {
        value: 'empty-search',
        label: 'No matches found. Try again or create a new service',
        type: ServiceTemplateOptionType.EmptySearch
      };
      const NEW_SERVICE_OPTION = {
        value: 'new-service',
        label: 'New Service',
        type: ServiceTemplateOptionType.NewService
      };
      const filteredOptions = options.filter(option => option.label.toLowerCase().includes(valueLabel.toLowerCase()));
      return [NEW_SERVICE_OPTION, ...(filteredOptions.length === 0 ? [NO_MATCHES_OPTION] : []), ...filteredOptions];
    },
    freeSolo: true,
    value: value,
    inputValue: valueLabel,
    getOptionLabel: getOptionLabel,
    getOptionSelected: getOptionSelected,
    onBlur: () => {
      if ((currentServiceTemplateOption === null || currentServiceTemplateOption === void 0 ? void 0 : currentServiceTemplateOption.type) !== ServiceTemplateOptionType.Service) {
        applyServiceOptionChange(null);
      }
    },
    onInputChange: (_, value, reason) => {
      if (reason === 'clear') {
        applyServiceOptionChange(null);
        return;
      }
      if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
      const option = serviceTemplateOptions.find(s => s.label === value);
      const fallbackServiceOptionValue = {
        value: '',
        label: value,
        type: ServiceTemplateOptionType.IncompleteSearch
      };
      applyServiceOptionChange(option !== null && option !== void 0 ? option : fallbackServiceOptionValue);
    },
    getOptionDisabled: option => option.type === ServiceTemplateOptionType.EmptySearch,
    onChange: async (event, value) => {
      const newValue = value ? value : null;
      if ((newValue === null || newValue === void 0 ? void 0 : newValue.type) === ServiceTemplateOptionType.NewService) {
        const {
          newService
        } = await openCreateServiceTemplateModal();
        const newServiceOption = newService ? {
          value: newService.id,
          label: newService.name,
          data: newService,
          type: ServiceTemplateOptionType.Service
        } : null;
        applyServiceOptionChange(newServiceOption);
      } else {
        applyServiceOptionChange(newValue);
      }
    }
  });
};